/**
 * A carousel slider component using Embla Carousel with scale animation effects
 * @see {@link https://www.embla-carousel.com/examples/predefined/#scale}
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode[]} props.children - Array of child elements to display as slides
 * @param {number} props.width - Width of the carousel container
 * @param {number} props.slideSize - Size of each individual slide
 * @returns {JSX.Element} Carousel slider component with scale animations
 */

import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { SliderControls } from './slider-controls'

import type { EmblaCarouselType, EmblaEventType } from 'embla-carousel'

const TWEEN_FACTOR_BASE = 0.1

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max)

type Props = {
  children: Array<React.ReactNode>
  slideSize?: number
}

export const SliderCarousel: React.FC<Props> = ({
  children,
  slideSize = 370,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const tweenFactor = useRef(0)
  const tweenNodes = useRef<Array<HTMLElement>>([])

  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi
      .slideNodes()
      .map(
        (slideNode) =>
          slideNode.querySelector('.embla__slide__number') as HTMLElement,
      )
  }, [])

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length
  }, [])

  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine()
      const scrollProgress = emblaApi.scrollProgress()
      const slidesInView = emblaApi.slidesInView()
      const isScrollEvent = eventName === 'scroll'

      for (const [snapIndex, scrollSnap] of emblaApi
        .scrollSnapList()
        .entries()) {
        let diffToTarget = scrollSnap - scrollProgress
        const slidesInSnap = engine.slideRegistry[snapIndex] ?? []

        for (const slideIndex of slidesInSnap) {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) {
            continue
          }

          if (engine.options.loop) {
            for (const loopItem of engine.slideLooper.loopPoints) {
              const target = loopItem.target()

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target)

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress)
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress)
                }
              }
            }
          }

          const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current)
          const scale = numberWithinRange(tweenValue, 0, 1).toString()
          const tweenNode = tweenNodes.current[slideIndex]
          tweenNode!.style.transform = `scale(${scale})`
        }
      }
    },
    [],
  )

  useEffect(() => {
    if (!emblaApi) {
      return
    }

    setTweenNodes(emblaApi)
    setTweenFactor(emblaApi)
    tweenScale(emblaApi)

    emblaApi
      .on('reInit', setTweenNodes)
      .on('reInit', setTweenFactor)
      .on('reInit', tweenScale)
      .on('scroll', tweenScale)
      .on('slideFocus', tweenScale)
  }, [emblaApi, tweenScale, setTweenNodes, setTweenFactor])

  return (
    <Root>
      <ViewPort ref={emblaRef}>
        <Container>
          {children.map((child, index) => (
            <Slide key={index} slideSize={slideSize}>
              <SlideItem className="embla__slide__number">{child}</SlideItem>
            </Slide>
          ))}
        </Container>
      </ViewPort>

      <SliderControls api={emblaApi!} />
    </Root>
  )
}

const Root = styled.div`
  width: var(--slider-width, 100%);

  margin: auto;
`

const ViewPort = styled.div`
  overflow: hidden;
`

const Container = styled.div`
  display: flex;
  touch-action: pan-y pinch-zoom;
`

const Slide = styled.div<{ slideSize: number }>`
  flex: 0 0 ${({ slideSize }) => slideSize}px;

  transform: translate3d(0, 0, 0);
  min-width: 0;
`

const SlideItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`
